import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import CartPrice from "./cartPrice";
import { jwtDecode } from "jwt-decode";
import Checkout from "./checkout";
import Payment from "./payment";
import Navbar from "../common/navbar";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Cart = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState("");
  const [page, setPage] = useState("Checkout");
  const [selectedAddress, setSelectedAddress] = useState("");
  const [totalAfterDiscount, setTotalAfterDiscount] = useState(0);
  const token = localStorage.getItem("STITCHD_ID");

  useEffect(() => {
    if (token) {
      const decoded = jwtDecode(token);
      setUserId(decoded._id);
    }
  },[token]);

  useEffect(() => {
    const fetchCartItems = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_URL}/user/${userId}/cart`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setItems(response.data.cart);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    if (userId) {
      fetchCartItems();
    }
  },[userId]);

  const handleQuantityChange = async (productId, newQuantity, size) => {
    try {
      // Ensure newQuantity is at least 1
      newQuantity = Math.max(1, newQuantity);

      await axios.patch(
        `${process.env.REACT_APP_URL}/user/cart/${userId}/${productId}/quantity`,
       
        { quantity: newQuantity, size: size }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Update the quantity locally in the state after successful API update
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.productId._id === productId && item.size === size
            ? { ...item, quantity: newQuantity }
            : item
        )
      );
    } catch (error) {
      console.error("Error updating quantity:", error);
      // Optionally, handle error feedback to the user
    }
  };

  const handleRemoveItem = async (productId, size) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_URL}/user/cart/${userId}/${productId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: { size },
        }
      );

      setItems((prevItems) =>
        prevItems.filter(
          (item) => !(item.productId._id === productId && item.size === size)
        )
      );
    } catch (error) {
      console.log("Error removing item:", error);
    }
  };

  const handleMoveToWishlist = async (productId, size) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_URL}/user/wishlist/${userId}/${productId}`,null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Product moved to wishlist", {
        position: "bottom-center",
        autoClose: 1000,
      });
    } catch (error) {
      toast.error("Error moving product to wishlist", {
        position: "bottom-center",
        autoClose: 1000,
      });
    }
    handleRemoveItem(productId, size);
  };

  const addressReceiver = (address) => {
    setSelectedAddress(address);
  };

  // Function to convert price string to number
  const parsePrice = (priceStr) => {
    if (!priceStr) return 0;
    return Number(priceStr.replace(/,/g, "").trim());
  };

  // Calculate total price
  const totalCartAmount = items.reduce(
    (total, item) => total + parsePrice(item.productId.price) * item.quantity,
    0
  );

  const cartPriceButton = (finalAmount) => {
    setTotalAfterDiscount(finalAmount);
    if (page === "Checkout") {
      setPage("PlaceOrder");
    } else if (page === "PlaceOrder") {
      if (selectedAddress) {
        setPage("Payment");
      } else {
        alert('Please select the delivery address')
      }
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-2 font-twentieth-century">
        <div className="flex justify-between mb-4 mt-20">
          <p className="text-xl text-gray-700 font-semibold">Shopping Cart</p>
          <Skeleton width={100} height={20} />
        </div>
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
            className="items-center border p-2 mb-4 rounded-lg shadow-md"
          >
            <div className="flex">
              <Skeleton width={100} height={150} />
              <div className="ml-4 flex flex-col flex-grow">
                <Skeleton width={`80%`} height={20} />
                <Skeleton width={`60%`} height={20} />
                <Skeleton width={`40%`} height={20} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="md:flex">
      <ToastContainer />
      <div
        className={`fixed top-0 left-0 w-full z-50 ${
          page !== "Checkout" ? "hidden md:block" : ""
        }`}
      >
        <Navbar />
      </div>
      {page === "Checkout" && (
        <div className="container mx-auto p-2 font-twentieth-century">
          <div className="flex justify-between mb-4 mt-20">
            <p className="text-xl text-gray-700 font-semibold">
              Shopping Cart ({items.length} Items)
            </p>
            <p className="text-lg text-gray-700 font-medium">
              Total: ₹{totalCartAmount}
            </p>
          </div>
          {items.length === 0 ? (
            <p className="text-center text-gray-500 h-screen  text-4xl">
              Your cart is empty.
            </p>
          ) : (
            items.map((item) => (
              <div
                key={item.productId._id}
                className="items-center border p-2 mb-4 rounded-lg shadow-md"
              >
                <div className="flex">
                  <Link to={`/product/${item.productId._id}`}>
                    <img
                      src={`${process.env.REACT_APP_URL}/${item.productId.images[0]}`}
                      alt={item.productId.name}
                      className="md:w-28 md:h-36 w-18 h-28 object-cover rounded-md"
                    />
                  </Link>
                  <div className="ml-4 flex flex-col flex-grow">
                    <div className="flex justify-between items-start">
                      <Link to={`/product/${item.productId._id}`}>
                        <h4 className="md:text-lg font-medium">
                          {item.productId.name}
                        </h4>
                      </Link>
                      <p className="text-gray-600 mb-2 ">
                        {item.productId.price}
                      </p>
                    </div>
                    <div className="flex text-sm md:text-md">
                      <p className="text-gray-500 flex">
                        Color:
                        {/* <div
                          className="h-4 w-4 mt-1"
                          style={{ backgroundColor: item.productId.color }}
                        ></div> */}
                        <p style={{ color: item.productId.color }} className="ml-1">{item.productId.color}</p>
                        |
                      </p>
                      <p className="text-gray-500 mb-2">Size: {item.size} |</p>
                      {item.productId.sleeves && (
                        <p className="text-gray-500 mb-2">
                          Sleeves: {item.productId.sleeves} |
                        </p>
                      )}
                    </div>
                    <div className="flex items-center md:space-x-2 md:text-md text-xs md:mt-1">
                      <p className="mr-1">QTY</p>
                      <button
                        className={`px-2 py-1 border rounded-md hover:bg-gray-200 ${
                          item.quantity === 1
                            ? "bg-gray-200 cursor-not-allowed"
                            : ""
                        }`}
                        onClick={() =>
                          handleQuantityChange(
                            item.productId._id,
                            item.quantity - 1,
                            item.size
                          )
                        }
                        disabled={item.quantity === 1}
                      >
                        -
                      </button>
                      <p className="px-4">{item.quantity}</p>
                      <button
                        className="px-2 py-1 border rounded-md hover:bg-gray-200"
                        onClick={() =>
                          handleQuantityChange(
                            item.productId._id,
                            item.quantity + 1,
                            item.size
                          )
                        }
                      >
                        +
                      </button>
                    </div>
                    <div className="">
                      <div className="h-px w-auto bg-gray-300 md:mt-4 mt-1"></div>
                      <div className="md:flex space-x-4">
                        <button
                          className="px-4 pt-2 text-gray-400 rounded-md hover:text-gray-600"
                          onClick={() =>
                            handleRemoveItem(item.productId._id, item.size)
                          }
                        >
                          Remove
                        </button>
                        <button
                          onClick={() =>
                            handleMoveToWishlist(item.productId._id, item.size)
                          }
                          className="px-4 pt-2 text-red-400 rounded-md hover:text-red-600"
                        >
                          Move to Wishlist
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {page === "PlaceOrder" && (
        <div className="flex flex-col mt-16 md:mt-20">
          <div className="h-auto w-auto border">
            <p className="font-sans font-semibold text-xl mb-2 ml-4">
              Select Delivery Address:
            </p>
            <Checkout addressReceiver={(selectedAddress) => addressReceiver(selectedAddress)} />
          </div>
        </div>
      )}
      <div className="flex flex-col">
        {items.length && (
          <CartPrice
            page={page}
            cartPriceButton={cartPriceButton}
            totalCartAmount={totalCartAmount}
            items={items}
          />
        )}
        {page === "PlaceOrder" && (
          <div className="h-auto w-auto border p-4 md:ml-4 hidden md:block">
            <p className="font-sans font-semibold text-xl mb-1 text-center">
              Payment Method
            </p>
            <Payment
              selectedAddress={selectedAddress}
              totalAfterDiscount={totalAfterDiscount}
              items={items}
            />
          </div>
        )}
        {page === "Payment" && (
          <div className="h-auto w-auto border p-4 md:hidden">
            <Payment
              selectedAddress={selectedAddress}
              totalAfterDiscount={totalAfterDiscount}
              items={items}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
